import { Button } from '@clearcover/atlas';
import { getIn, useFormikContext } from 'formik';
import { FormValuesType } from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers';
import React, { FC, useMemo } from 'react';
import { FormInput } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { StateOptionsType } from 'Shared/constants/states';
import { FormErrorBanner } from '../FormErrorBanner';

type AddressFormType = {
  address: FormValuesType['address'];
};

export const Address: FC<{
  allowStateChange?: boolean;
  editable?: boolean;
  stateOptions?: StateOptionsType;
  tooltip?: string;
}> = ({ stateOptions, allowStateChange = true, editable = true, tooltip }) => {
  const { details, appId } = useAppContext();
  const {
    status,
    setStatus,
    values,
    setValues,
    setFieldValue,
    touched,
    errors,
    handleBlur,
  } = useFormikContext<AddressFormType>();
  const [, postAddressOverride] = useLazyQuoteApi({
    method: 'POST',
    url: `/insurance_applications/${appId}/override_address_outcome`,
  });

  const isAddressOverrideCommandAvailable = useMemo(
    () =>
      details.commands.addresses.some(
        (addressCommand) =>
          addressCommand.name === 'override_address_outcome' &&
          addressCommand.runnable
      ),
    [details.commands.addresses]
  );

  const getFullFieldName = (fieldName: string) =>
    `address.${details.principalInsuredId}.${fieldName}`;

  return (
    <div data-testid="residential-address-form">
      {status && status.isInvalidAddress && (
        <FormErrorBanner
          message="Your address is missing data or could not be verified. Please enter a valid residential address to continue."
          actionButton={
            isAddressOverrideCommandAvailable && (
              <Button
                variant="danger-secondary"
                size="sm"
                data-testid="override-address-button"
                onClick={() => {
                  postAddressOverride().then(() => {
                    setStatus({ ...status, isInvalidAddress: false });
                  });
                }}
              >
                Override
              </Button>
            )
          }
        />
      )}
      <FormInput
        variant="addressAutocomplete"
        prefix="address"
        stateOptions={stateOptions}
        allowStateChange={allowStateChange}
        values={{
          address1: values.address[details.principalInsuredId].address1,
          address2: values.address[details.principalInsuredId].address2,
          city: values.address[details.principalInsuredId].city,
          state: values.address[details.principalInsuredId].state,
          zip: values.address[details.principalInsuredId].zip,
        }}
        fieldNames={{
          address1: getFullFieldName('address1'),
          address2: getFullFieldName('address2'),
          city: getFullFieldName('city'),
          state: getFullFieldName('state'),
          zip: getFullFieldName('zip'),
        }}
        onAddressSelect={(selectedOption) => {
          const { city, streetLine, state, zipcode } = selectedOption;
          setValues({
            ...values,
            address: {
              [details.principalInsuredId]: {
                ...values.address[details.principalInsuredId],
                address1: streetLine,
                city,
                state,
                zip: zipcode,
              },
            },
          });
          setStatus({ ...status, isInvalidAddress: false });
        }}
        onAddressLine1Change={(value) => {
          setFieldValue(getFullFieldName('address1'), value);
          setStatus({ ...status, isInvalidAddress: false });
        }}
        onAddressLine2Change={(value) => {
          setFieldValue(getFullFieldName('address2'), value);
          setStatus({
            ...status,
            isInvalidAddress: false,
            isMissingLineTwo: false,
          });
        }}
        onCityChange={(value) => {
          setFieldValue(getFullFieldName('city'), value);
          setStatus({ ...status, isInvalidAddress: false });
        }}
        onStateChange={(value) => {
          setFieldValue(getFullFieldName('state'), value);
          setStatus({ ...status, isInvalidAddress: false });
        }}
        onZipChange={(value) => {
          setFieldValue(getFullFieldName('zip'), value);
          setStatus({ ...status, isInvalidAddress: false });
        }}
        errorMessages={{
          address1: getIn(touched, getFullFieldName('address1'))
            ? getIn(errors, getFullFieldName('address1'))
            : undefined,
          address2:
            getIn(errors, getFullFieldName('address2')) ||
            (status.isMissingLineTwo ? 'Line 2 is required' : undefined),
          city: getIn(touched, getFullFieldName('city'))
            ? getIn(errors, getFullFieldName('city'))
            : undefined,
          state: getIn(touched, getFullFieldName('state'))
            ? getIn(errors, getFullFieldName('state'))
            : undefined,
          zip: getIn(touched, getFullFieldName('zip'))
            ? getIn(errors, getFullFieldName('zip'))
            : undefined,
        }}
        onBlur={handleBlur}
        disabled={!editable}
        readOnly={!editable}
        tooltip={tooltip}
      />
    </div>
  );
};
